import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Products.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';

const ProductsList = forwardRef(({ onSelectProduct, selectedProductId, onNew }, ref) => {
  const { t } = useTranslation('products');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [productTypes, setProductTypes] = useState([]);

  useImperativeHandle(ref, () => ({
    fetchProducts
  }));

  useEffect(() => {
    fetchProducts();
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    fetchProductTypes();
  }, []);

  const fetchProductTypes = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/product-types/?page_size=100'));
      if (response.ok) {
        const data = await response.json();
        setProductTypes(data.product_types || []);
      }
    } catch (error) {
      console.error('Error fetching product types:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/products?page_size=100'));
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      } else {
        setError(t('list.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(t('list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = (product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType = typeFilter === 'all' || String(product.product_type_id) === typeFilter;

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && product.is_active) ||
                         (statusFilter === 'inactive' && !product.is_active);

    return matchesSearch && matchesType && matchesStatus;
  });

  const getTypeBadge = (productType) => {
    const typeUpper = productType?.toUpperCase();
    let className = 'category-badge ';

    switch (typeUpper) {
      case 'SLEEVE':
        className += 'type-sleeve';
        break;
      case 'SEAL':
        className += 'type-seal';
        break;
      case 'SHRINK':
        className += 'type-shrink';
        break;
      case 'HASL':
        className += 'type-hasl';
        break;
      case 'CONTAINER':
        className += 'type-container';
        break;
      case 'MIXED':
        className += 'type-mixed';
        break;
      default:
        className += 'type-default';
    }

    // Product types are user-defined records (Admin → Tipos de Producto): show the stored name
    return <span className={className}>{productType || t('list.defaultValues.notAvailable')}</span>;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDimensions = (product) => {
    return product.dimensions || t('list.defaultValues.notAvailable');
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && products.length === 0) {
    return <div className="list-container"><div className="loading">{t('list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('list.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('list.newProduct')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('list.filters.allTypes')}</option>
            {productTypes.map(type => (
              <option key={type.product_type_id} value={String(type.product_type_id)}>
                {type.type_name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('list.filters.allStatus')}</option>
            <option value="active">{t('list.filters.active')}</option>
            <option value="inactive">{t('list.filters.inactive')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('list.headers.productCode')}</th>
              <th>{t('list.headers.productName')}</th>
              <th>{t('list.headers.customer')}</th>
              <th>{t('list.headers.type')}</th>
              <th>{t('list.headers.unitOfMeasure')}</th>
              <th>{t('list.headers.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm ? t('list.noData.noSearchResults') : t('list.noData.noProducts')}
                </td>
              </tr>
            ) : (
              filteredProducts.map(product => (
                <tr
                  key={product.product_id}
                  onClick={() => onSelectProduct(product.product_id)}
                  className={selectedProductId === product.product_id ? 'selected' : ''}
                >
                  <td className="product-sku">
                    <code>{product.product_code || t('list.defaultValues.notAvailable')}</code>
                  </td>
                  <td className="product-name">
                    <strong>{product.product_name || t('list.defaultValues.unnamedProduct')}</strong>
                  </td>
                  <td className="customer-name">
                    {product.customer_name || t('list.defaultValues.noValue')}
                  </td>
                  <td>
                    {getTypeBadge(product.product_type)}
                  </td>
                  <td className="text-center">
                    <span className="uom-badge">{t(`uom.${product.unit_of_measure}`) || product.unit_of_measure || 'PCS'}</span>
                  </td>
                  <td className="text-center">
                    {product.is_active ? (
                      <span className="status-badge status-success">{t('list.status.active')}</span>
                    ) : (
                      <span className="status-badge status-error">{t('list.status.inactive')}</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('list.footer.recordCount', { filtered: filteredProducts.length, total: products.length })}
        </div>
      </div>
    </div>
  );
});

ProductsList.displayName = 'ProductsList';

export default ProductsList;